import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Instagram, Send, Heart, Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { NAV_LINKS } from "../lib/content";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative mt-10 bg-purpleDark text-white overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <Star className="absolute left-[10%] top-8 animate-floaty" size={30} fill="white" />
        <Heart className="absolute right-[14%] top-16 animate-floatySlow" size={26} fill="white" />
        <Star className="absolute right-[30%] bottom-10 animate-wiggle" size={22} fill="white" />
      </div>

      {/* newsletter */}
      <div className="relative max-w-5xl mx-auto px-6 pt-16">
        <div className="rounded-[2rem] bg-goldK text-ink p-8 md:p-10 shadow-2xl text-center">
          <h3 className="text-2xl md:text-3xl font-bold">Join the Kingdom Newsletter</h3>
          <p className="mt-2 text-ink/80">Get stories, activities and magic delivered to your inbox.</p>
          <form onSubmit={subscribe} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address" data-testid="newsletter-email"
              className="flex-1 rounded-full px-5 py-3.5 bg-white text-ink outline-none border-2 border-transparent focus:border-purpleK" />
            <button disabled={loading} data-testid="newsletter-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-purpleK text-white font-display font-semibold px-6 py-3.5 shadow-[0_5px_0_#5b2a86] hover:-translate-y-0.5 active:translate-y-1 transition-transform">
              <Send size={18} /> {loading ? "Sending..." : "Sign Up"}
            </button>
          </form>
          <div className="mt-5 pt-4 border-t border-ink/10">
            <p className="text-ink/70 text-sm font-display font-semibold mb-2">Prefer just one topic? Subscribe by interest:</p>
            <div className="flex flex-wrap justify-center gap-2" data-testid="footer-segments">
              {[["books", "Books"], ["new-books", "New Books"], ["reading-challenges", "Reading Challenges"], ["new-games", "New Games"], ["events", "Events"], ["parents", "Parents"], ["schools", "Schools & Educators"], ["news", "Kingdom News"]].map(([slug, label]) => (
                <Link key={slug} to={`/join/${slug}`} data-testid={`footer-segment-${slug}`}
                  className="rounded-full bg-white/70 hover:bg-purpleK hover:text-white text-ink/80 text-xs font-display font-semibold px-3.5 py-1.5 transition-colors">{label}</Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* links */}
      <div className="relative max-w-7xl mx-auto px-6 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-goldK text-purpleDark"><Crown size={24} strokeWidth={2.5} /></span>
            <span className="font-display font-bold text-xl">Sir Gingerman</span>
          </Link>
          <p className="mt-4 text-white/70 leading-relaxed">A magical Kingdom where children read, learn, grow and build confidence.</p>
          <div className="flex gap-3 mt-5">
            <a href="https://instagram.com/sirgingerman" target="_blank" rel="noopener noreferrer" aria-label="Instagram @sirgingerman" data-testid="social-instagram"
              className="grid place-items-center h-11 w-11 rounded-2xl bg-white/10 hover:bg-goldK hover:text-ink transition-colors">
              <Instagram size={20} />
            </a>
            <a href="https://www.tiktok.com/@sirgingerman" target="_blank" rel="noopener noreferrer" aria-label="TikTok @sirgingerman" data-testid="social-tiktok"
              className="grid place-items-center h-11 w-11 rounded-2xl bg-white/10 hover:bg-goldK hover:text-ink transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M16.5 3c.3 2.1 1.5 3.6 3.5 3.9v2.5c-1.3.1-2.5-.3-3.6-.9v5.6c0 3.2-2.4 5.4-5.4 5.4A5.3 5.3 0 0 1 5.7 14a5.3 5.3 0 0 1 6.3-5.2v2.7a2.6 2.6 0 0 0-3.6 2.5 2.6 2.6 0 0 0 5.2.1V3h2.9z"/></svg>
            </a>
            <span className="hidden md:inline-flex items-center text-white/50 text-sm font-semibold ml-1">@sirgingerman</span>
          </div>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-white/80 hover:text-goldK font-display transition-colors">{l.label}</Link>
          ))}
          <Link to="/teacher-hub" data-testid="footer-institutions" className="text-white/80 hover:text-goldK font-display transition-colors">For Schools &amp; Institutions</Link>
        </div>
        <div>
          <h4 className="font-display font-semibold text-lg text-goldK">Say Hello</h4>
          <p className="mt-3 text-white/70">hello@sirgingerman.com</p>
          <Link to="/contact" className="inline-block mt-4 rounded-full bg-white/10 hover:bg-white/20 px-5 py-2.5 font-display font-semibold transition-colors">Contact Us</Link>
        </div>
      </div>

      <div className="relative border-t border-white/10 pt-6 pb-2 flex flex-wrap justify-center gap-x-5 gap-y-2 text-white/60 text-sm" data-testid="footer-legal">
        {[["consent", "Parent Consent"], ["privacy", "Privacy Policy"], ["terms", "Terms of Use"], ["cookies", "Cookie Policy"], ["data-deletion", "Data Deletion"], ["copyright", "Copyright & IP"]].map(([s, l]) => (
          <Link key={s} to={`/${s}`} className="hover:text-goldK transition-colors">{l}</Link>
        ))}
        <Link to="/kingdom/login" className="hover:text-goldK transition-colors">Member Login</Link>
      </div>
      <div className="relative py-4 text-center text-white/60 text-xs leading-relaxed max-w-3xl mx-auto px-4">
        <p>© 2024–2026 U Genius LLC. All Rights Reserved.</p>
        <p className="mt-1">Sir Gingerman™ and Sir Gingerman's Kingdom™ are trademarks of U Genius LLC. Trademark applications are currently pending. Unauthorized use of these trademarks or any confusingly similar marks is prohibited.</p>
        <p className="mt-2 text-white/50">Made with <Heart className="inline" size={13} fill="#FFC839" stroke="#FFC839" /> for little hearts.</p>
      </div>
    </footer>
  );
}
